import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  useScrollTrigger,
  Slide,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Science as ScienceIcon,
  Dashboard as DashboardIcon,
  Groups as CommunityIcon,
  Verified as CertIcon,
  Translate as TranslateIcon,
  AutoAwesome as AutoAwesomeIcon,
  Upload as UploadIcon,
  TableChart as DatasetIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';
import { useThemeMode } from '../../context/ThemeContext';

// Navbar que se oculta al hacer scroll hacia abajo
function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, logout } = useAuthStore();
  const { mode, setMode } = useThemeMode();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNav, setAnchorElNav] = useState(null);

  const toggleTheme = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    if (isAuthenticated) {
      fetch('/api/user/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ theme: next }),
      }).catch(() => {});
    }
  };

  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);
  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);

  const handleLogout = () => {
    logout();
    handleCloseUserMenu();
    navigate('/');
  };

  // Cuentas con alcance 'datos' (invitados) solo ven el módulo Datos.
  const datosOnly = user?.scope === 'datos';
  // (sus secciones están en las pestañas del módulo, que son su navegación principal: la barra queda limpia)
  const navItems = datosOnly ? [] : [
    { label: 'Inicio', path: '/', icon: <ScienceIcon /> },
    { label: 'Proyectos', path: '/proyectos', icon: <DashboardIcon /> },
    { label: 'Datos', path: '/datos', icon: <DatasetIcon /> },
    { label: 'Comunidad', path: '/comunidad', icon: <CommunityIcon /> },
    { label: 'Certificados', path: '/certificados', icon: <CertIcon /> },
    { label: 'Traducir', path: '/traducir-proyectos', icon: <TranslateIcon /> },
    { label: 'Generar con IA', path: '/comparte', icon: <AutoAwesomeIcon />, highlight: true },
    { label: 'Subir proyecto', path: '/subir-proyecto', icon: <UploadIcon /> },
  ];

  const isActive = (path) => location.pathname === path || (path === '/datos' && location.pathname.startsWith('/datos/') && !location.pathname.startsWith('/datos/subir'));

  return (
    <HideOnScroll>
      <AppBar position="fixed">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: 70 }}>
            {/* Logo - Desktop */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  cursor: 'pointer',
                  mr: 4,
                }}
                onClick={() => navigate(datosOnly ? '/datos' : '/')}
              >
                <ScienceIcon
                  sx={{
                    mr: 1.25,
                    fontSize: 30,
                    bgcolor: 'primary.main',
                    borderRadius: 2,
                    p: 0.6,
                    color: 'primary.contrastText',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, letterSpacing: '-0.02em', color: 'text.primary' }}
                >
                  SciDataHub
                </Typography>
              </Box>
            </motion.div>

            {/* Mobile Menu (las cuentas «Solo Datos» navegan con las pestañas del módulo) */}
            {!datosOnly && <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 'auto' }}>
              <IconButton onClick={handleOpenNavMenu}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{ display: { xs: 'block', md: 'none' } }}
              >
                <MenuItem onClick={() => { toggleTheme(); handleCloseNavMenu(); }}>
                  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                  <Typography sx={{ ml: 1 }}>{mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}</Typography>
                </MenuItem>
                {navItems.map((item) => (
                  <MenuItem 
                    key={item.path} 
                    onClick={() => { navigate(item.path); handleCloseNavMenu(); }}
                  >
                    {item.icon}
                    <Typography sx={{ ml: 1 }}>{item.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>}

            {/* Logo - Mobile */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mr: datosOnly ? 'auto' : 0, cursor: 'pointer' }}
              onClick={() => navigate(datosOnly ? '/datos' : '/')}>
              <ScienceIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" fontWeight={700} color="text.primary">
                SciDataHub
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
              {navItems.map((item) => (
                <motion.div key={item.path} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                  {item.highlight ? (
                    <Button
                      onClick={() => navigate(item.path)}
                      startIcon={item.icon}
                      sx={{
                        ml: 0.5,
                        px: { md: 1.25, xl: 2 },
                        py: 1.1,
                        whiteSpace: 'nowrap',
                        borderRadius: 2,
                        fontWeight: 700,
                        color: '#fff',
                        background: 'linear-gradient(95deg, #0e7c66 0%, #1d9e75 55%, #36c08e 100%)',
                        boxShadow: '0 4px 14px rgba(14,124,102,0.35)',
                        backgroundSize: '180% 100%',
                        animation: 'iaShine 6s ease infinite',
                        '@keyframes iaShine': {
                          '0%': { backgroundPosition: '0% 50%' },
                          '50%': { backgroundPosition: '100% 50%' },
                          '100%': { backgroundPosition: '0% 50%' },
                        },
                        '&:hover': { boxShadow: '0 6px 20px rgba(14,124,102,0.5)', filter: 'brightness(1.05)' },
                      }}
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate(item.path)}
                      sx={{
                        color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                        fontWeight: isActive(item.path) ? 600 : 500,
                        px: { md: 1, xl: 2 },
                        py: 1.1,
                        whiteSpace: 'nowrap',
                        borderRadius: 2,
                        backgroundColor: (t) => (isActive(item.path) ? alpha(t.palette.primary.main, 0.1) : 'transparent'),
                        '&:hover': {
                          backgroundColor: (t) => alpha(t.palette.primary.main, isActive(item.path) ? 0.14 : 0.06),
                        },
                      }}
                      startIcon={item.icon}
                    >
                      {item.label}
                    </Button>
                  )}
                </motion.div>
              ))}
            </Box>

            {/* User Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
                <IconButton
                  onClick={toggleTheme}
                  size="medium"
                  aria-label={mode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' }, // en móvil está en el menú: aquí desbordaba la barra
                    color: 'text.primary',
                    bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.12 : 0.08),
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.2 : 0.14),
                    },
                  }}
                >
                  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              {isAuthenticated && !datosOnly && (
                <Tooltip title="Notificaciones">
                  <IconButton
                    onClick={() => navigate('/dashboard')}
                    aria-label="Notificaciones"
                    sx={{
                      color: 'text.primary',
                      '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
                    }}
                  >
                    <NotificationsIcon />
                  </IconButton>
                </Tooltip>
              )}
              {isAdmin && (
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Tooltip title="Panel Admin">
                    <IconButton 
                      onClick={() => navigate('/admin')}
                      sx={{
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        '&:hover': { bgcolor: 'secondary.dark' },
                      }}
                    >
                      <AdminIcon />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              )}

              {isAuthenticated ? (
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Tooltip title="Abrir menú">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          fontWeight: 600,
                          fontSize: 15,
                        }}
                      >
                        {user?.username?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </motion.div>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<LoginIcon />}
                    onClick={() => navigate('/login')}
                    sx={{ borderRadius: 2 }}
                  >
                    Entrar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RegisterIcon />}
                    onClick={() => navigate('/register')}
                    sx={{ borderRadius: 2 }}
                  >
                    Registro
                  </Button>
                </Box>
              )}

              {/* User Menu */}
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    borderRadius: 3,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    minWidth: 200,
                  },
                }}
              >
                <MenuItem disabled sx={{ opacity: 0.7 }}>
                  <Typography variant="body2" color="text.secondary">
                    {user?.username}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => { toggleTheme(); handleCloseUserMenu(); }}>
                  {mode === 'dark' ? <LightModeIcon sx={{ mr: 1.5, fontSize: 20 }} /> : <DarkModeIcon sx={{ mr: 1.5, fontSize: 20 }} />}
                  {mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                </MenuItem>
                {(isAdmin || user?.datosAdmin) && (
                  <MenuItem onClick={() => { navigate('/datos/admin'); handleCloseUserMenu(); }}>
                    <DatasetIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Administración de Datos
                  </MenuItem>
                )}
                {(isAdmin || user?.certAdmin) && (
                  <MenuItem onClick={() => { navigate('/certificacion'); handleCloseUserMenu(); }}>
                    <CertIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Certificación en blockchain
                  </MenuItem>
                )}
                {/* una cuenta «Solo Datos» no tiene tablero, perfil ni configuración: esas opciones la sacarían del módulo */}
                {!datosOnly && (
                  <MenuItem onClick={() => { navigate('/dashboard'); handleCloseUserMenu(); }}>
                    <DashboardIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Dashboard
                  </MenuItem>
                )}
                {!datosOnly && (
                  <MenuItem onClick={() => { navigate(`/profile/${user?.username || ''}`); handleCloseUserMenu(); }}>
                    <PersonIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Mi perfil
                  </MenuItem>
                )}
                {!datosOnly && (
                  <MenuItem onClick={() => { navigate('/dashboard/settings'); handleCloseUserMenu(); }}>
                    <SettingsIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Configuración
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem onClick={() => { navigate('/admin/subir'); handleCloseUserMenu(); }}>
                    <UploadIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Subir Proyecto
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  Cerrar sesión
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </HideOnScroll>
  );
};

export default Navbar;